"use client";

import type { CSSProperties, HTMLAttributes } from "react";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { cn } from "../../lib";
import { MorphEngine, type MorphEngineOptions } from "./morph-engine";
import { MORPH_DEFAULTS, type MorphTransition } from "./morph-shaders";

export type { MorphTransition };

export interface MorphImageProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	/** Image shown when `showAfter` is false. */
	beforeSrc: string;
	/** Image shown when `showAfter` is true. */
	afterSrc: string;
	/**
	 * Which side is active. Morphs when this flips (after both textures load).
	 * Defaults to `false` (before).
	 */
	showAfter?: boolean;
	transition?: MorphTransition;
	duration?: number;
	ease?: string;
	intensity?: number;
	scale?: number;
	aberration?: number;
	drift?: number;
	overlayColor?: string;
	/**
	 * When true (default), destroy the WebGL engine after each morph and idle
	 * on a static `<img>` — no continuous RAF. Set false only if you need the
	 * canvas to keep drifting while idle.
	 */
	disposeAfterMorph?: boolean;
	/** Decorative alt for the static image. */
	alt?: string;
	/** object-fit for the static image / underlay. */
	objectFit?: CSSProperties["objectFit"];
}

type MorphPhase = "idle" | "morphing";

/**
 * Morph between two images (no slider chrome).
 *
 * **Idle = static photo** (default): WebGL mounts only for a theme/src flip,
 * then tears down after the transition so battle/HUD stays cheap. Extensible
 * via `disposeAfterMorph={false}` if a surface needs a persistent canvas.
 */
export function MorphImage({
	beforeSrc,
	afterSrc,
	showAfter = false,
	transition = MORPH_DEFAULTS.transition,
	duration = MORPH_DEFAULTS.duration,
	ease = MORPH_DEFAULTS.ease,
	intensity = MORPH_DEFAULTS.intensity,
	scale = MORPH_DEFAULTS.scale,
	aberration = MORPH_DEFAULTS.aberration,
	drift = MORPH_DEFAULTS.drift,
	overlayColor = MORPH_DEFAULTS.overlayColor,
	disposeAfterMorph = true,
	alt = "",
	objectFit = "cover",
	className,
	style,
	...props
}: MorphImageProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const underlayRef = useRef<HTMLImageElement>(null);
	const engineRef = useRef<MorphEngine | null>(null);
	const [webglFailed, setWebglFailed] = useState(false);
	const [phase, setPhase] = useState<MorphPhase>("idle");
	const [morphEpoch, setMorphEpoch] = useState(0);
	const [displaySrc, setDisplaySrc] = useState(() =>
		showAfter ? afterSrc : beforeSrc,
	);

	const showAfterRef = useRef(showAfter);
	const prevShowAfterRef = useRef<boolean | null>(null);
	const morphFromRef = useRef(displaySrc);
	const morphToRef = useRef(displaySrc);

	showAfterRef.current = showAfter;

	const optsRef = useRef<MorphEngineOptions>({
		transition,
		duration,
		ease,
		intensity,
		scale,
		aberration,
		drift,
		overlayColor,
		loop: false,
	});
	optsRef.current = {
		transition,
		duration,
		ease,
		intensity,
		scale,
		aberration,
		drift,
		overlayColor,
		loop: false,
	};

	const activeSrc = showAfter ? afterSrc : beforeSrc;
	const sameSrc = beforeSrc === afterSrc;

	useEffect(() => {
		if (phase !== "idle") {
			return;
		}
		setDisplaySrc(activeSrc);
	}, [activeSrc, phase]);

	useEffect(() => {
		if (prevShowAfterRef.current === null) {
			prevShowAfterRef.current = showAfter;
			setDisplaySrc(activeSrc);
			return;
		}
		if (prevShowAfterRef.current === showAfter) {
			return;
		}
		prevShowAfterRef.current = showAfter;

		if (sameSrc || webglFailed) {
			setDisplaySrc(activeSrc);
			return;
		}

		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reducedMotion) {
			setDisplaySrc(activeSrc);
			return;
		}

		// Side flip: from = previous theme asset, to = new (restart-safe).
		morphFromRef.current = showAfter ? beforeSrc : afterSrc;
		morphToRef.current = showAfter ? afterSrc : beforeSrc;
		setMorphEpoch((epoch) => epoch + 1);
		setPhase("morphing");
	}, [showAfter, activeSrc, beforeSrc, afterSrc, sameSrc, webglFailed]);

	useEffect(() => {
		if (phase !== "morphing" || sameSrc || webglFailed) {
			return undefined;
		}
		const el = containerRef.current;
		if (!el) {
			setDisplaySrc(morphToRef.current);
			setPhase("idle");
			return undefined;
		}

		let cancelled = false;
		const fromSrc = morphFromRef.current;
		const toSrc = morphToRef.current;

		const engine = MorphEngine.create(el, {
			items: [{ image: fromSrc }, { image: toSrc }],
			startIndex: 0,
			reducedMotion: false,
			dprCap: 2,
			alpha: true,
			getOptions: () => optsRef.current,
		});
		if (!engine) {
			setWebglFailed(true);
			setDisplaySrc(toSrc);
			setPhase("idle");
			return undefined;
		}
		engineRef.current = engine;

		const tearDownToIdle = () => {
			if (disposeAfterMorph) {
				engine.destroy();
				if (engineRef.current === engine) {
					engineRef.current = null;
				}
			}
			setPhase("idle");
		};

		/**
		 * Canvas still covers the underlay until the static `<img>` has the
		 * final frame committed + decoded — otherwise destroy() reveals one
		 * frame of the pre-morph underlay (flicker).
		 */
		const finish = () => {
			if (cancelled) {
				return;
			}
			cancelled = true;

			flushSync(() => {
				setDisplaySrc(toSrc);
			});

			const underlay = underlayRef.current;
			const afterPaint = () => {
				requestAnimationFrame(() => {
					// Effect cleanup already tore this engine down.
					if (engineRef.current !== engine) {
						return;
					}
					tearDownToIdle();
				});
			};

			if (underlay != null && typeof underlay.decode === "function") {
				void underlay.decode().then(afterPaint).catch(afterPaint);
				return;
			}
			afterPaint();
		};

		void engine.whenTexturesReady().then(() => {
			if (cancelled) {
				return;
			}
			engine.goToIndex(1, { onComplete: finish });
		});

		return () => {
			cancelled = true;
			engine.destroy();
			if (engineRef.current === engine) {
				engineRef.current = null;
			}
		};
	}, [phase, morphEpoch, sameSrc, webglFailed, disposeAfterMorph]);

	const showCanvas = phase === "morphing" && !webglFailed && !sameSrc;

	return (
		<div
			className={cn("relative h-full w-full overflow-hidden", className)}
			style={style}
			{...props}
			data-morph-phase={phase}
		>
			<img
				ref={underlayRef}
				src={displaySrc}
				alt={alt}
				className="pointer-events-none absolute inset-0 z-0 h-full w-full"
				style={{ objectFit }}
				draggable={false}
			/>
			{showCanvas ? (
				<div
					ref={containerRef}
					className="absolute inset-0 z-[1]"
					aria-hidden
				/>
			) : null}
		</div>
	);
}
